import _isEmpty from 'lodash/isEmpty';
import type { color } from 'types/shared/worlds';

export enum ExportStatus {
  READY_WITH_ISSUES = 'ready_with_issues',
  FAILED = 'failed',
  RESOLVED = 'resolved',
}

// API response status values
export enum ApiExportStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
}

const getColor = (
  type: 'expense' | 'revenue' | 'transaction' | 'taxes' | undefined,
): 'expenses' | 'revenues' | 'bank' | 'taxes' => {
  switch (type) {
    case 'expense':
      return 'expenses';
    case 'revenue':
      return 'revenues';
    case 'transaction':
      return 'bank';
    default:
      return 'taxes';
  }
};

const bgColorMap = {
  expenses: 'bg-expenses-100',
  revenues: 'bg-revenues-100',
  bank: 'bg-bank-100',
  taxes: 'bg-taxes-100',
} as const;

const getBgColor = (
  type: 'expense' | 'revenue' | 'transaction' | 'taxes' | undefined,
): string => {
  return bgColorMap[getColor(type)];
};

/** Stepper UI classes per universe (connect flow). Full literal strings so Tailwind can scan them. */
const stepperClassesMap = {
  expenses: {
    iconWrapper:
      'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 bg-expenses-600',
    highlightedBg: 'rounded-lg bg-expenses-50 py-2',
    highlightedText: 'text-base font-normal text-expenses-700',
    text: 'text-expenses',
    hoverText: 'hover:text-expenses-700',
  },
  revenues: {
    iconWrapper:
      'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 bg-revenues-600',
    highlightedBg: 'rounded-lg bg-revenues-50 py-2',
    highlightedText: 'text-base font-normal text-revenues-700',
    text: 'text-revenues',
    hoverText: 'hover:text-revenues-700',
  },
  bank: {
    iconWrapper:
      'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 bg-bank-600',
    highlightedBg: 'rounded-lg bg-bank-50 py-2',
    highlightedText: 'text-base font-normal text-bank-700',
    text: 'text-bank',
    hoverText: 'hover:text-bank-700',
  },
  taxes: {
    iconWrapper:
      'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 bg-taxes-600',
    highlightedBg: 'rounded-lg bg-taxes-50 py-2',
    highlightedText: 'text-base font-normal text-taxes-700',
    text: 'text-taxes',
    hoverText: 'hover:text-taxes-700',
  },
} as const;

const getStepperClasses = (color: color | undefined) =>
  stepperClassesMap[color as keyof typeof stepperClassesMap] ??
  stepperClassesMap.taxes;

/** Card styles for integration selection (hover border + arrow color). Fancy shadow is always applied on the card. */
const cardClassesMap = {
  expenses: {
    hoverClasses: 'hover:border-expenses-400 focus:border-expenses-400',
    arrowColorClass: 'text-expenses-600',
  },
  revenues: {
    hoverClasses: 'hover:border-revenues-400 focus:border-revenues-400',
    arrowColorClass: 'text-revenues-600',
  },
  bank: {
    hoverClasses: 'hover:border-bank-400 focus:border-bank-400',
    arrowColorClass: 'text-bank-600',
  },
  taxes: {
    hoverClasses: 'hover:border-taxes-400 focus:border-taxes-400',
    arrowColorClass: 'text-taxes-600',
  },
} as const;

const getCardClasses = (color: color | undefined) =>
  cardClassesMap[color as keyof typeof cardClassesMap] ?? cardClassesMap.taxes;

const getTypeFromPathname = (
  pathname: string,
): 'expense' | 'revenue' | 'transaction' | 'taxes' => {
  switch (true) {
    case pathname.includes('/expenses'):
      return 'expense';
    case pathname.includes('/revenues'):
      return 'revenue';
    case pathname.includes('/banks'):
      return 'transaction';
    default:
      return 'taxes';
  }
};

const getFileTypeFromPathname = (
  pathname: string,
): 'expenses' | 'revenues' | 'transactions' | undefined => {
  switch (true) {
    case pathname.includes('/expenses'):
      return 'expenses';
    case pathname.includes('/revenues'):
      return 'revenues';
    case pathname.includes('/banks'):
      return 'transactions';
    default:
      return undefined;
  }
};

const encodeCategoryId = (originalCategoryId: string) =>
  `#${originalCategoryId?.replaceAll('.', '___')}`;

const revertCategoryId = (categoryId: string) =>
  categoryId?.replaceAll('___', '.')?.replaceAll('#', '');

const adaptCategories = (
  categories: any[],
  guessedCategoriesData: any[],
  prefix: string,
) =>
  categories
    ?.map((category) => {
      const guessed = guessedCategoriesData?.find(
        (_category: any) => _category.categoryId === category.id,
      );

      return {
        ...category,
        ...(guessed || {}),
        id: `${prefix || 'categories'}.${encodeCategoryId(category.id)}`,
      };
    })
    ?.filter((category) => category.required);

const isExportReadyWithIssues = (exportData: any) => {
  const errors = [
    ...(exportData?.result?.chiftExport?.expense_errors || []),
    ...(exportData?.result?.chiftExport?.invoice_errors || []),
    ...(exportData?.result?.chiftExport?.transaction_errors || []),
  ];

  return exportData?.status === ApiExportStatus.SUCCESS && !_isEmpty(errors);
};

const formatIntegrationName = (slug: string): string => {
  const cleanSlug = slug.replace(/-chift$/i, '');
  return cleanSlug.charAt(0).toUpperCase() + cleanSlug.slice(1);
};

export {
  getColor,
  getBgColor,
  getStepperClasses,
  getCardClasses,
  getTypeFromPathname,
  getFileTypeFromPathname,
  encodeCategoryId,
  revertCategoryId,
  adaptCategories,
  isExportReadyWithIssues,
  formatIntegrationName,
};
